import { FunctionComponent, useEffect, useState } from 'react'
import { CaretDownIcon, LinkIcon } from '@phosphor-icons/react'
import { AppCard, AppTag } from '@/shared/ui'
import { TaskSteps } from '../../enumerations/task-steps.enumeration'
import { MAP_JOB_COLORS } from '../map/map-constants'

/**
 * Properties type for JobMapCard
 */
export type JobMapCardProps = {
  jobNumber: string
  address?: string
  slot?: string
  route?: string
  origin?: string
  operationModel?: string
  client?: string
  value?: string
  partialStatus?: TaskSteps
  loading?: boolean
  selected?: boolean
  onShowDetails?: (sd: boolean) => void
  copy?: (text: string) => string
  onTitleClick?: () => void
}

export type JobMapCardLabels = {
  [key: string]: string
}

/**
 * Label texts.
 */
const LABELS: JobMapCardLabels = {
  destinationAddress: 'Destination address:',
  slot: 'Slot:',
  route: 'Route:',
  origin: 'Origin:',
  operationModel: 'Operative model',
  client: 'Client',
  orderValue: 'Order value',
  orderDetails: 'Order details'
}

/**
 * Component that job data for jobs map.
 *
 * @param param0 {JobMapCardProps} properties
 */
export const JobMapCard: FunctionComponent<JobMapCardProps> = ({
  jobNumber,
  address,
  slot,
  route,
  origin,
  operationModel,
  client,
  value,
  partialStatus = TaskSteps.PICKING,
  loading = false,
  selected = false,
  onShowDetails = (): void => {},
  copy = (text: string): string => (LABELS[text] ? LABELS[text] : text),
  onTitleClick = () => {}
}: JobMapCardProps): JSX.Element => {
  const [showDetails, setShowDetaiils] = useState(false)

  useEffect(() => {
    setShowDetaiils(selected)
  }, [selected])

  const getInfoLabel = (label?: string, text?: string) => {
    if (!text) {
      return ''
    }

    return (
      <div className='gungnir-jmc-info'>
        <b>{`${label}${label?.endsWith(':') ? '' : ':'}`}</b> {text}
      </div>
    )
  }

  const getLabels = () => {
    if (showDetails) {
      return (
        <>
          {getInfoLabel(copy('route'), route)}
          {getInfoLabel(copy('origin'), origin)}
          {getInfoLabel(copy('operationModel'), copy(`job-models.${operationModel}`))}
          {getInfoLabel(copy('client'), client)}
          {getInfoLabel(copy('orderValue'), value)}
        </>
      )
    }

    return null
  }

  const statusBg = MAP_JOB_COLORS[partialStatus]?.bg ?? '#2B8A3E'

  return (
    <div className={`gungnir-job-map-card ${showDetails ? 'selected' : ''}`}>
      <AppCard
        className='gungnir-jmc-job-card'
        title={
          <a onClick={() => onTitleClick()}>
            {jobNumber} <LinkIcon />
          </a>
        }
        extra={
          partialStatus ? (
            <AppTag
              style={{
                backgroundColor: statusBg,
                color: '#ffffff',
                border: 'none'
              }}
            >
              {copy(`${partialStatus}`)}
            </AppTag>
          ) : (
            ''
          )
        }
        loading={loading}
        actions={[
          <div
            key='gungnir-jmc-details-action'
            className={`gungnir-jmc-details-action ${showDetails ? 'selected' : ''}`}
            onClick={() => {
              setShowDetaiils(!showDetails)
              if (onShowDetails) {
                onShowDetails(!showDetails)
              }
            }}
          >
            {copy('orderDetails')}
            <CaretDownIcon className='gungnir-jmc-details-icon' />
          </div>
        ]}
      >
        {getInfoLabel(copy('destinationAddress'), address)}
        {getInfoLabel(copy('slot'), slot)}
        {getLabels()}
      </AppCard>
    </div>
  )
}
